import { config } from '../shared/config';
import { currentAccessToken } from './supabase';

/**
 * Every stored file (NTO, waivers, NOC, receipts…) is served from
 * `${apiUrl}/files/…` and the API only hands a file to its owner, so the
 * request must carry the Supabase bearer token. A plain <a href> opened in a
 * new tab carries no Authorization header and the API answers
 * "Missing bearer token".
 *
 * Rather than rewrite every link, one listener catches clicks on links into
 * /files/, fetches the file with the token, and opens it from memory. The
 * links keep their real href, so middle-click semantics and tests are unchanged.
 */
export function isApiFileLink(href: string | null | undefined): href is string {
  if (!href) return false;
  return href.startsWith(`${config.apiUrl}/files/`) || href.startsWith('/files/');
}

export async function openAuthedFile(href: string, target: Window | null): Promise<void> {
  const url = href.startsWith('http') ? href : `${config.apiUrl}${href}`;
  const token = await currentAccessToken();
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    target?.close();
    const body = await res.text().catch(() => '');
    let message = `Could not open the file (${res.status}).`;
    try {
      const parsed = JSON.parse(body) as { error?: string };
      if (parsed.error) message = `Could not open the file: ${parsed.error}`;
    } catch {
      /* not JSON */
    }
    window.alert(message);
    return;
  }
  const blobUrl = URL.createObjectURL(await res.blob());
  if (target && !target.closed) {
    target.location.href = blobUrl;
  } else {
    // Pop-up blocked: fall back to a download in this tab.
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = url.split('/').pop() ?? 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Give the new tab time to load before releasing the memory.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

let installed = false;

export function installAuthedFileLinks(doc: Document = document): void {
  if (installed) return;
  installed = true;
  doc.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      const anchor = (event.target as Element | null)?.closest?.('a');
      const href = anchor?.getAttribute('href');
      if (!isApiFileLink(href)) return;
      event.preventDefault();
      // Open the tab synchronously, inside the click, so pop-up blockers allow it.
      const target = window.open('', '_blank');
      if (target) target.document.title = 'Opening document…';
      void openAuthedFile(href, target);
    },
    true,
  );
}
